import React from 'react';
import { Square, Maximize2, AlertCircle } from 'lucide-react';

interface ARWallAnglePromptProps {
  isOpen: boolean;
  onConfirm: (areWalls90Degrees: boolean) => void;
  onCancel: () => void;
}

const ARWallAnglePrompt: React.FC<ARWallAnglePromptProps> = ({
  isOpen,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-violet-700 text-white px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <AlertCircle size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Property Wall Configuration</h2>
              <p className="text-purple-100 text-sm mt-0.5">Before we start scanning</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-6">
          <p className="text-slate-700 text-base mb-6">
            Are all the walls in this property at <strong>90-degree angles</strong> (standard rectangular rooms)?
          </p>

          {/* Info boxes */}
          <div className="space-y-3 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-500 text-white p-1.5 rounded">
                  <Square size={18} />
                </div>
                <div>
                  <p className="font-semibold text-green-900 text-sm mb-1">Standard 90° Walls</p>
                  <p className="text-green-700 text-xs">
                    Typical rectangular rooms with perpendicular walls. Most properties fall into this category.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="bg-amber-500 text-white p-1.5 rounded">
                  <Maximize2 size={18} />
                </div>
                <div>
                  <p className="font-semibold text-amber-900 text-sm mb-1">Non-Standard Angles</p>
                  <p className="text-amber-700 text-xs">
                    Unusual room shapes, angled walls, or bay windows. Less common architectural features.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onConfirm(true)}
              className="group px-5 py-3.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <Square size={18} className="group-hover:scale-110 transition-transform" />
              <span>Yes, 90°</span>
            </button>

            <button
              onClick={() => onConfirm(false)}
              className="group px-5 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-700 transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <Maximize2 size={18} className="group-hover:scale-110 transition-transform" />
              <span>No, Angled</span>
            </button>
          </div>

          {/* Cancel button */}
          <button
            onClick={onCancel}
            className="w-full mt-3 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-sm font-medium"
          >
            Cancel Scan
          </button>
        </div>
      </div>
    </div>
  );
};

export default ARWallAnglePrompt;
